import React, { useState } from 'react';

interface InstallGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  embedCode: string;
}

type TabKey = 'wordpress' | 'wix' | 'html';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'wordpress', label: 'WordPress' },
  { key: 'wix', label: 'Wix' },
  { key: 'html', label: 'HTML直接' },
];

const TAB_STEPS: Record<TabKey, string[]> = {
  wordpress: [
    'WordPress管理画面で「外観 → テーマファイルエディター」を開きます',
    'footer.php の </body> 直前に下のコードを貼り付けます',
    '「ファイルを更新」を押すと、サイトにチャットボットが表示されます',
  ],
  wix: [
    'Wixエディター → 設定 → カスタムコード → 「ページのコードを追加」を開きます',
    '</body> の直前に下のコードを貼り付けます',
    '「適用」して公開するとチャットボットが表示されます',
  ],
  html: [
    'サイトのHTMLファイルをテキストエディターで開きます',
    '</body> の直前に下のコードを貼り付けます',
    'ファイルを保存してブラウザで確認します',
  ],
};

export const InstallGuideModal: React.FC<InstallGuideModalProps> = ({ isOpen, onClose, embedCode }) => {
  const [activeTab, setActiveTab] = useState<TabKey>('wordpress');
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(embedCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const steps = TAB_STEPS[activeTab];

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
      data-testid="install-guide-modal"
    >
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)' }} onClick={onClose} />
      <div style={{
        position: 'relative',
        background: '#fff',
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(15,23,42,0.25)',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflowY: 'auto',
        padding: '32px',
      }}>
        {/* ヘッダー */}
        <div style={{ marginBottom: '8px' }}>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#0F172A', marginBottom: '6px' }}>サイトへの設置ガイド</div>
          <div style={{ fontSize: '13px', color: '#0a7a72' }}>ご利用の環境を選んで、設置方法をご確認ください。</div>
        </div>

        {/* タブ (pill style) */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '20px', marginBottom: '24px' }}>
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              data-testid={`install-guide-tab-${key}`}
              style={{
                background: activeTab === key ? '#0a7a72' : '#fff',
                color: activeTab === key ? '#fff' : '#334155',
                border: `1.5px solid ${activeTab === key ? '#0a7a72' : '#CBD5E1'}`,
                borderRadius: '999px',
                fontSize: '13px',
                fontWeight: activeTab === key ? 700 : 400,
                padding: '6px 18px',
                cursor: 'pointer',
                transition: 'background 0.15s, color 0.15s, border-color 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ステップ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
          {steps.map((text, i) => (
            <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <span style={{
                flexShrink: 0,
                width: '26px',
                height: '26px',
                borderRadius: '50%',
                background: '#E0F2F1',
                color: '#0a7a72',
                fontSize: '13px',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>{i + 1}</span>
              <span style={{ fontSize: '14px', color: '#334155', lineHeight: '1.7', paddingTop: '3px' }}>{text}</span>
            </div>
          ))}
        </div>

        {/* コードブロック */}
        <div style={{ background: '#1E293B', borderRadius: '8px', padding: '14px 16px', marginBottom: '24px' }}>
          <pre style={{
            color: '#E2E8F0',
            fontSize: '12px',
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            margin: 0,
          }}>
            {embedCode}
          </pre>
        </div>

        {/* アクションバー */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={handleCopy}
            data-testid="install-guide-copy-button"
            style={{
              background: '#fff',
              color: '#334155',
              border: '1.5px solid #CBD5E1',
              borderRadius: '8px',
              padding: '10px 22px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {copied ? 'コピー済み' : 'コードをコピー'}
          </button>
          <button
            onClick={onClose}
            data-testid="install-guide-close-button"
            style={{
              background: '#0a7a72',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 22px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};
